import {
  Box,
  Button,
  Checkbox,
  DialogActions,
  DialogContent,
  FormControl,
  FormControlLabel,
  Grid2,
  MenuItem,
  Select,
  Typography
} from '@mui/material'
import FloatingTitle from 'components/badges/FloatingTitle'
import { InviteLinkRow } from 'components/badges/InviteLinkBadge'
import SegmentButton, { SegmentItem } from 'components/buttons/SegmentButton'
import { RANGE } from 'components/calendar/CalendarHeader'
import { PopupSectionRow } from 'components/popup/PopupSectionRow'
import { useCalendar } from 'components/providers/CalendarProvider'
import { usePayment } from 'components/providers/PaymentProvider'
import { tierFeaturesToAnalyticsFeature } from 'components/tier/OutOfTierDialog'
import { tierToPlan } from 'components/tier/TierHover'
import i18n from 'i18n'
import { t } from 'i18next'
import React, { useState } from 'react'
import LocalStorage from 'services/LocalStorage'
import { AccountDoc, GroupDoc, UserSettingsDoc } from 'types/api/changes'
import { CalendarRange } from 'types/components/calendar'
import { LocalStorageKey } from 'types/services/localStorage'
import { highestActiveTier, Tier } from 'utils/api/accounts'

import { APP_COLOR } from 'utils/colors'

import { StyledDialog } from '../styled.dialog'
import GroupsPicker from './GroupsPicker'
import SizeTextFieldInput from './styled'
import { TierFeatures } from 'utils/api/group_state'

interface EmbedCalendarDialogProps {
  group: GroupDoc
  open: boolean
  handleClose: () => void
  userSettings: UserSettingsDoc
  account: AccountDoc
}

const defaultPercent = '80'
const defaultPixel = '600'

enum EmbedType {
  IFRAME = 'iframe',
  WP_CODE = 'WP_CODE',
  WP_WIDGET = 'WP_WIDGET',
  WIX = 'WIX'
}

const EmbedCalendarDialog: React.FC<EmbedCalendarDialogProps> = ({
  group,
  open,
  handleClose,
  userSettings,
  account
}) => {
  const payments = usePayment()

  const [width, setWidth] = useState(defaultPercent)
  const [height, setHeight] = useState(defaultPercent)
  const [selectedValue, setSelectedValue] = useState(RANGE[1].value as string)
  const [showHeader, setShowHeader] = useState(false)
  const [removeBranding, setHideBranding] = useState(false)
  const [selectedGroups, setSelectedGroups] = useState<GroupDoc[]>([group])
  const [calendarTitle, setCalendarTitle] = useState(group.Name)
  const [embedType, setEmbedType] = useState<EmbedType>(EmbedType.IFRAME)

  const embedTypes: SegmentItem[] = [
    {
      value: EmbedType.IFRAME,
      title: 'iframe'
    },
    {
      value: EmbedType.WP_CODE,
      title: 'WordPress - ShortCode'
    },
    {
      value: EmbedType.WP_WIDGET,
      title: 'WordPress - Block Widget'
    },
    {
      value: EmbedType.WIX,
      title: 'Wix widget'
    }
  ]

  const segmentItems: SegmentItem[] = [
    {
      value: 'px',
      title: 'px'
    },
    {
      value: '%',
      title: '%'
    }
  ]

  const [widthType, setWidthType] = useState<SegmentItem>(segmentItems[1])
  const [heightType, setHeightType] = useState<SegmentItem>(segmentItems[1])

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target

    let modified = Number(value)

    if (modified > 2000) modified = 2000
    if (name === 'input1') {
      if (widthType.value === '%') {
        if (modified > 100) modified = 100
      }

      setWidth(`${modified}`)
    } else if (name === 'input2') {
      if (heightType.value === '%') {
        if (modified > 100) modified = 100
      }

      setHeight(`${modified}`)
    } else if (name === 'calendarTitle') {
      setCalendarTitle(value)
    }
  }

  const handleSelectChange = (event: string) => {
    setSelectedValue(event)
    calendar.onView(event as CalendarRange)
  }

  const calendar = useCalendar()
  let groupLink = ``

  const baseUrl = process.env.REACT_APP_LINK_DOMAIN
  const commonParams = `view=${selectedValue}&showHeader=${
    showHeader ? '1' : '0'
  }&calendarTitle=${encodeURIComponent(calendarTitle)}&removeBranding=${
    removeBranding ? '1' : '0'
  }`

  if (selectedGroups.length > 1) {
    const groupIds = selectedGroups.map((group) => group._id).join(',')
    groupLink = `${baseUrl}/calendars?groupIds=${groupIds}&${commonParams}`
  } else {
    groupLink = `${baseUrl}/calendar/${selectedGroups[0]._id}?${commonParams}`
  }

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setShowHeader(event.target.checked)
  }

  const iFrameWidth = widthType.value === 'px' ? `${width}px` : `${width}%`
  const iFrameHeight = heightType.value === 'px' ? `${height}px` : `${height}%`

  const iFrameString = `<iframe src="${groupLink}" width="${iFrameWidth}" frameborder="0" height="${iFrameHeight}"></iframe>`

  let embedString = ''

  if (embedType === EmbedType.IFRAME) {
    embedString = iFrameString
  } else if (embedType === EmbedType.WP_CODE) {
    embedString = `[groupcal_iframe url="${groupLink}" width="${iFrameWidth.replace(
      '%',
      ''
    )}" height="${iFrameHeight.replace('%', '')}"]`
  } else if (embedType === EmbedType.WP_WIDGET || embedType === EmbedType.WIX) {
    embedString = groupLink
  }

  return (
    <StyledDialog
      disableEscapeKeyDown
      open={open}
      maxWidth="md"
      fullWidth
      onClose={handleClose}
      PaperProps={{
        style: {
          height: '90%',
          maxHeight: '90%',
          width: '90%',
          maxWidth: '90%',
          borderRadius: 8
        }
      }}
    >
      <DialogContent
        sx={{
          display: 'flex',
          height: '100vh',
          paddingBottom: '0px'
        }}
      >
        <Box
          sx={{
            maxWidth: '333px',
            paddingTop: '1rem',
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            marginRight: i18n.dir() === 'ltr' ? '16px' : undefined,
            marginLeft: i18n.dir() === 'rtl' ? '16px' : undefined
          }}
          gap={4}
        >
          <Box
            gap={2}
            display={'flex'}
            flexDirection={'row'}
            justifyItems={'center'}
            alignItems={'center'}
          >
            <SizeTextFieldInput
              name="input1"
              value={width}
              label={`${t('width')}`}
              onChange={handleInputChange}
            />
            <SegmentButton
              items={segmentItems}
              selected={widthType}
              onSelect={function (segmentItem: SegmentItem): void {
                setWidthType(segmentItem)
                if (segmentItem.value === '%') setWidth(defaultPercent)
                else setWidth(defaultPixel)
              }}
            />
          </Box>
          <Box
            gap={2}
            display={'flex'}
            flexDirection={'row'}
            justifyItems={'center'}
            alignItems={'center'}
          >
            <SizeTextFieldInput
              label={`${t('height')}`}
              name="input2"
              value={height}
              onChange={handleInputChange}
            />
            <SegmentButton
              items={segmentItems}
              selected={heightType}
              onSelect={function (segmentItem: SegmentItem): void {
                setHeightType(segmentItem)
                if (segmentItem.value === '%') setHeight(defaultPercent)
                else setHeight(defaultPixel)
              }}
            />
          </Box>

          <div>
            <Grid2 display={'flex'} flexDirection={'row'} gap={2}>
              <FormControl fullWidth sx={{ position: 'relative' }}>
                <Typography
                  position={'absolute'}
                  fontSize={'0.75rem'}
                  color={'#848484'}
                  sx={{
                    marginLeft: i18n.dir() === 'ltr' ? 2 : undefined,
                    marginRight: i18n.dir() === 'rtl' ? 2 : undefined,
                    paddingLeft: '4px',
                    paddingRight: '4px',
                    marginTop: -2,
                    backgroundColor: 'white',
                    background: 'white',
                    zIndex: 10000
                  }}
                >
                  {t('defaultView')}
                </Typography>
                <Select
                  value={selectedValue}
                  sx={{ zIndex: 9999 }}
                  onChange={(event) => {
                    handleSelectChange(event.target.value)
                  }}
                  onOpen={() => {
                    LocalStorage.set(LocalStorageKey.SHOULD_KEEP_POPUP, '1')
                  }}
                  onClose={() => {
                    LocalStorage.set(LocalStorageKey.SHOULD_KEEP_POPUP, '0')
                  }}
                  MenuProps={{
                    style: {
                      zIndex: 9999
                    }
                  }}
                >
                  {RANGE.map((item) => (
                    <MenuItem
                      key={item.value?.toString()}
                      autoFocus={false}
                      value={item.value as string}
                    >
                      {item.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid2>
            <FormControl sx={{ marginTop: 4 }} fullWidth>
              <SizeTextFieldInput
                type="text"
                name="calendarTitle"
                maxLength={25}
                value={calendarTitle}
                label={t('calendarName')}
                onChange={handleInputChange}
              />
            </FormControl>
          </div>
          <FormControlLabel
            sx={{ margin: 0 }}
            control={
              <Checkbox
                sx={{ padding: 0 }}
                checked={showHeader}
                onChange={handleCheckboxChange}
              />
            }
            label={
              <Typography
                marginLeft={i18n.dir() === 'ltr' ? 2 : 0}
                marginRight={i18n.dir() === 'rtl' ? 2 : 0}
              >
                {t('showHeader')}
              </Typography>
            }
          />
          <Box
            flexDirection={'row'}
            display={'flex'}
            justifyContent={'flex-start'}
            alignContent={'start'}
          >
            <Checkbox
              sx={{
                paddingTop: 0,
                paddingBottom: 0,
                paddingRight: i18n.dir() === 'ltr' ? 2 : 0,
                paddingLeft: i18n.dir() === 'rtl' ? 2 : 0
              }}
              onClick={
                highestActiveTier(account) === Tier.FREE
                  ? (e) => {
                      // open payment dialog
                      payments.showPaymentDialog(
                        tierToPlan(Tier.PRO),
                        handleClose,
                        [
                          tierFeaturesToAnalyticsFeature(
                            TierFeatures.REMOVE_BRANDING
                          )
                        ]
                      )
                    }
                  : (e) => {
                      setHideBranding((e.target as any).checked)
                    }
              }
              checked={removeBranding}
            />
            <PopupSectionRow
              neededTier={Tier.PRO}
              account={account}
              action={() => {
                setHideBranding(!removeBranding)
              }}
              tierFeature={TierFeatures.REMOVE_BRANDING}
              title={
                <Typography fontSize={'16px'} fontFamily={'NotoSans'}>
                  {t('removeGroupcalBranding')}
                </Typography>
              }
            />
          </Box>

          <GroupsPicker
            userSettings={userSettings}
            account={account}
            selectedGroups={selectedGroups}
            openedFromGroup={group}
            onGroupToggle={(group) => {
              if (selectedGroups.find((g) => g._id === group._id)) {
                setSelectedGroups(
                  selectedGroups.filter((g) => g._id !== group._id)
                )
              } else {
                setSelectedGroups([...selectedGroups, group])
              }
            }}
          />
        </Box>
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          <Box
            sx={{ width: '800px' }}
            display={'flex'}
            flexDirection={'column'}
            paddingLeft={4}
            paddingRight={4}
            gap={4}
          >
            <Typography>{t('toDisplayCalendar')}</Typography>
            <Box
              display={'flex'}
              flexDirection={'row'}
              alignItems={'center'}
              gap={3}
              position={'relative'}
            >
              <FloatingTitle title={t('typeOfCode')} />
              <Select
                value={embedType}
                sx={{
                  zIndex: 9999,
                  borderRadius: '10px',
                  backgroundColor: 'white',
                  minWidth: '17rem',
                  '& .MuiOutlinedInput-notchedOutline': {
                    border: '1.5px solid #e4e4e4'
                  }
                }}
                onChange={(event) => {
                  setEmbedType(event.target.value as EmbedType)
                }}
                onOpen={() => {
                  LocalStorage.set(LocalStorageKey.SHOULD_KEEP_POPUP, '1')
                }}
                onClose={() => {
                  LocalStorage.set(LocalStorageKey.SHOULD_KEEP_POPUP, '0')
                }}
                MenuProps={{
                  style: {
                    zIndex: 9999
                  }
                }}
              >
                {embedTypes.map((item) => (
                  <MenuItem
                    key={item.value?.toString()}
                    autoFocus={false}
                    value={item.value as string}
                  >
                    {item.title}
                  </MenuItem>
                ))}
              </Select>
              <InviteLinkRow
                height="55px"
                textSize="16px"
                text={embedString}
                link={iFrameString}
              />
            </Box>
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexGrow: 1
            }}
            minWidth={'100px'}
            minHeight={'100px'}
            width={'100%'}
          >
            <iframe
              id="calendar-embed-iframe"
              src={`${groupLink}&preview=1`}
              width={iFrameWidth}
              height={iFrameHeight}
              frameBorder="0"
            />
          </Box>
        </div>
      </DialogContent>
      <DialogActions>
        <Button
          id="close-embed-button"
          sx={{ color: APP_COLOR }}
          onClick={handleClose}
        >{`${t('close')}`}</Button>
      </DialogActions>
    </StyledDialog>
  )
}

export default EmbedCalendarDialog
